// lib/services/capture/capture_service.ts

import ColorCameraPool from "./color_camera_pool";
import CameraInfo, { CameraFacing } from "./camerainfo";
import { YuvFormat, YuvUploadDescriptor } from "../compute/yuvdefs";
import YuvManager from "../compute/yuvmanager";
import Stage from "../stage/stage";

// At the top of capture_service.ts
const PREFERRED_VIDEO_WIDTH  = 1280;
const PREFERRED_VIDEO_HEIGHT = 720;
const MIN_VIDEO_WIDTH        = 1280;
const MIN_VIDEO_HEIGHT       = 720;
const MAX_VIDEO_WIDTH        = 1280;
const MAX_VIDEO_HEIGHT       = 720;

type ExtendedCapabilities = MediaTrackCapabilities & {
  focusMode?: string[];
  focusDistance?: MediaSettingsRange;
};

type ExtendedConstraints = MediaTrackConstraints & {
  focusMode?: ConstrainDOMString;
  focusDistance?: ConstrainDouble;
};

/**
 * Owns the ColorCameraPool and drives one frame upload per camera frame.
 *
 * Two execution modes:
 *   - "worker": iOS Safari path. A Worker reads VideoFrames from a
 *     MediaStreamTrackProcessor, calls frame.copyTo(buffer), and posts
 *     the resulting bytes + plane layout back to the main thread, which
 *     does the GPU write. (YUV plane access from VideoFrame is only
 *     allowed on a worker in Safari.)
 *   - "main": Chromium/Android path. Same logic runs on the main thread
 *     using the standard MediaStreamTrackProcessor API.
 *
 * The service exposes the pool publicly so consumers (StickerCompute,
 * video render pipeline) can lease or peek slots as needed.
 *
 * Initialization order:
 *   1. new CaptureService()        -- cheap, no GPU, no permissions.
 *   2. await init()                -- platform detect + camera enumeration.
 *   3. (optional) set_selected()   -- UI overrides default camera choice.
 *   4. await start()               -- user-gesture handler triggers
 *                                     getUserMedia and begins frame pumping.
 *   5. stop()                      -- stops capture but leaves the service
 *                                     reusable for another start().
 *   6. shutdown()                  -- destroys pool, fully tears down.
 */
export default class CaptureService {
  // Pool of GPU camera-frame textures.
  camera_pool: ColorCameraPool;

  // The "active" camera info — driven into the pool / capture stream.
  camera_info: CameraInfo;

  // Enumeration results.
  cameralist: CameraInfo[] = [];
  selected: CameraInfo | null = null;

  private initialized: boolean = false;
  private capturing: boolean = false;

  private worker: Worker | null = null;
  private worker_mode: boolean = false;

  private stream: MediaStream | null = null;
  private processor: any | null = null; // MediaStreamTrackProcessor
  private reader: ReadableStreamDefaultReader<VideoFrame> | null = null;

  private yuv_manager: YuvManager;
  has_logged_first_frame:boolean = false;

  constructor() {
    this.camera_pool = new ColorCameraPool();
    this.camera_info = new CameraInfo();
    this.yuv_manager = new YuvManager();
  }

  // ---- Lifecycle ----------------------------------------------------------

  /**
   * Idempotent. Detects platform, enumerates cameras, and picks a default.
   * Does NOT request camera permission for capture itself.
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    this.worker_mode = this.detect_ios();

    await this.enumerate_cameras();
    this.select_default_camera();

    this.initialized = true;
  }

  /**
   * Tear down completely. Stops capture, releases pool textures.
   */
  shutdown(): void {
    this.stop();
    this.camera_pool.shutdown();
    this.initialized = false;
  }

  is_initialized(): boolean { return this.initialized; }
  is_capturing(): boolean { return this.capturing; }

  // ---- Enumeration -------------------------------------------------------

  /**
   * Builds cameralist from navigator.mediaDevices.enumerateDevices. Each
   * device becomes a CameraInfo with whatever fields we can infer
   * pre-permission. Label may be empty until the user grants access at
   * least once on this origin.
   */
  
private async enumerate_cameras(): Promise<void> {
  this.cameralist = [];

  if (!navigator?.mediaDevices?.enumerateDevices) {
    console.warn("CaptureService.enumerate_cameras: mediaDevices not available");
    return;
  }

  // Step 1: open a generic stream to trigger / verify camera permission.
  // We don't care which camera answers — we just need an active video
  // track so subsequent enumerateDevices() returns real labels.
  let permission_stream: MediaStream | null = null;
  try {
    permission_stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: "environment" }, // hint, not required
    });
  } catch (err) {
    console.warn("CaptureService.enumerate_cameras: permission denied", err);
    return;
  }

  try {
    // Step 2: enumerate while the stream is still active. Labels are
    // populated only when at least one video track is live.
    let devices: MediaDeviceInfo[] = [];
    try {
      devices = await navigator.mediaDevices.enumerateDevices();
    } catch (err) {
      console.error("CaptureService.enumerate_cameras: enumerate failed", err);
      return;
    }

    for (const d of devices) {
      if (d.kind !== "videoinput") continue;

      const info = new CameraInfo();
      info.device_id = d.deviceId;
      info.label = d.label || "";
      info.facing = this.guess_facing(d.label);

      // Probe each specific camera. Stream is closed after each probe.
      await this.probe_capabilities(info);

      this.cameralist.push(info);
    }
  } finally {
    // Step 3: close the permission stream now that enumeration is done.
    for (const t of permission_stream.getTracks()) t.stop();
  }
}

  /**
   * Pick a default camera. Preference order: back-facing > unknown > front.
   * Mirrors selected fields into this.camera_info so start() has a single
   * active reference.
   */
  private select_default_camera(): void {
    if (this.cameralist.length === 0) {
      this.selected = null;
      return;
    }

    const back = this.cameralist.find((c) => c.facing === "back");
    const unknown = this.cameralist.find((c) => c.facing === "unknown");
    const front = this.cameralist.find((c) => c.facing === "front");

    this.selected = back ?? unknown ?? front ?? this.cameralist[0];
    this.copy_camera_info(this.selected, this.camera_info);
  }

  /**
   * UI hook to switch active camera. Caller should stop() and start()
   * again if currently capturing.
   */
  set_selected(info: CameraInfo): void {
    this.selected = info;
    this.copy_camera_info(info, this.camera_info);
  }

  private copy_camera_info(src: CameraInfo, dst: CameraInfo): void {
    dst.device_id = src.device_id;
    dst.label = src.label;
    dst.facing = src.facing;
    dst.format = src.format;
    dst.width = src.width;
    dst.height = src.height;
    dst.y_width = src.y_width;
    dst.y_height = src.y_height;
    dst.chroma_width = src.chroma_width;
    dst.chroma_height = src.chroma_height;
    dst.framerate = src.framerate;
    dst.probe = src.probe;
  }

  private guess_facing(label: string): CameraFacing {
    const l = label.toLowerCase();
    if (l.includes("back") || l.includes("rear") || l.includes("environment")) return "back";
    if (l.includes("front") || l.includes("user") || l.includes("face")) return "front";
    return "unknown";
  }

  /**
   * Optional capability probe. Opens a short-lived stream just to read
   * width/height/framerate, then closes it. May prompt for permission; on
   * permission denial we silently leave fields at zero and continue.
   *
   * Comment this call out in enumerate_cameras if you want to defer
   * probing until the user explicitly starts capture.
   */
  
 is_android(): boolean {
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent ?? "";
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? "";

  return /Android/i.test(ua) || /Android/i.test(platform);
}

preferred_yuv_format(): "I420" | "NV12" {
  return this.is_android() ? "I420" : "NV12";
}

  private async probe_capabilities(info: CameraInfo): Promise<void> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        deviceId: { exact: info.device_id },
        width:  { ideal: MAX_VIDEO_WIDTH,  max: PREFERRED_VIDEO_WIDTH },
        height: { ideal: MAX_VIDEO_HEIGHT, max: PREFERRED_VIDEO_HEIGHT },
        aspectRatio: 16 / 9,
        frameRate: { ideal: 30, max: 30 },
      },
    });
    const track = stream.getVideoTracks()[0];
    const settings = track.getSettings();
    info.width = settings.width ?? 0;
    info.height = settings.height ?? 0;
    info.framerate = settings.frameRate ?? 0;
    info.format =  this.preferred_yuv_format();
    track.stop();
    stream.getTracks().forEach((t) => t.stop());
  } catch (err) {
    console.warn(`CaptureService.probe_capabilities failed for ${info.label}`, err);
  }
}

  // ---- Capture lifecycle -------------------------------------------------

 

private async trySetClosestFocus(track: MediaStreamTrack): Promise<void> {
  const caps =
    (typeof track.getCapabilities === "function"
      ? track.getCapabilities()
      : {}) as ExtendedCapabilities;

  const constraints: ExtendedConstraints = {};

  if (Array.isArray(caps.focusMode) && caps.focusMode.includes("manual")) {
    constraints.focusMode = "manual";
  }

  if (caps.focusDistance && typeof caps.focusDistance.min === "number") {
    constraints.focusDistance = caps.focusDistance.min;
  }

  if (!("focusMode" in caps) && !("focusDistance" in caps)) {
    console.log("[capture] no camera focus controls exposed");
    return;
  }

  try {
    await track.applyConstraints(constraints);
    console.log("[capture] applied closest focus", track.getSettings());
  } catch (err) {
    console.warn("[capture] failed to apply focus constraints", err);
  }
}

  /**
   * Begin capturing from the active camera (this.camera_info.device_id).
   * Must be called from within a user-gesture handler on iOS Safari.
   */
  async start(): Promise<void> {
    if (this.capturing) return;

    console.log("[capture] start() called, worker_mode=", this.worker_mode, "device_id=", this.camera_info.device_id);

    if (!this.camera_info.device_id) {
      throw new Error("CaptureService.start: camera_info.device_id is empty");
    }

    const constraints: MediaStreamConstraints = {
      audio: false,
      video: {
        deviceId: { exact: this.camera_info.device_id },
        width:  { ideal: MAX_VIDEO_WIDTH,  max: PREFERRED_VIDEO_WIDTH },
        height: { ideal: MAX_VIDEO_HEIGHT, max: PREFERRED_VIDEO_HEIGHT },
        aspectRatio: 16 / 9,
      },
    };

    this.stream = await navigator.mediaDevices.getUserMedia(constraints);
    const track = this.stream.getVideoTracks()[0];

     console.log("[capture] got stream, track=", track?.label, "settings=", track?.getSettings());
     
  // CRITICAL: set this before kicking off the pump, since the pump's
  // while loop reads it.
   this.capturing = true;
   await this.trySetClosestFocus(track);

    if (this.worker_mode) {
      await this.start_worker_capture(track);
    } else {
      await this.start_main_capture(track);
    }

    this.capturing = true;
      console.log("[capture] start() done, capturing=true");
  }

  /**
   * Stops capture, terminates worker if any. Pool textures remain
   * allocated so a subsequent start() can reuse them.
   */
  stop(): void {
    if (!this.capturing) return;

    if (this.reader) {
      try { this.reader.cancel(); } catch {}
      this.reader = null;
    }
    this.processor = null;

    if (this.stream) {
      for (const t of this.stream.getTracks()) t.stop();
      this.stream = null;
    }

    if (this.worker) {
      this.worker.postMessage({ type: "stop" });
      this.worker.terminate();
      this.worker = null;
    }

    this.camera_pool.clear();

    this.capturing = false;
  }

  // ---- Main-thread capture path -------------------------------------------

  private async start_main_capture(track: MediaStreamTrack): Promise<void> {
    const MSTP = (window as any).MediaStreamTrackProcessor;
     console.log("[capture] MediaStreamTrackProcessor available?", !!MSTP);
    if (!MSTP) {
      throw new Error(
        "MediaStreamTrackProcessor not available. Use the worker path or upgrade the browser."
      );
    }
    this.processor = new MSTP({ track });
    this.reader = this.processor.readable.getReader();
     console.log("[capture] reader acquired, starting pump");

    // Pull frames as fast as they arrive.
    this.pump_main().catch((err) => {
      console.error("CaptureService.pump_main failed", err);
    });
  }

  private async pump_main(): Promise<void> {
     let frame_count = 0;
    while (this.capturing && this.reader) {
      const { value: frame, done } = await this.reader.read();
      if (done)
      {
         console.log("[capture] pump: reader done");
         break;
      }
      if (!frame)
        {
            console.log("[capture] pump: empty frame"); 
           continue;
       }
       if (frame_count < 3)
       {
          console.log("[capture] pump received frame", frame_count, frame.codedWidth, "x", frame.codedHeight);
          frame_count++;
       }

      await this.on_video_frame(frame);
    }
     console.log("[capture] pump exited");
  }

  // ---- Worker-thread capture path (iOS) ----------------------------------

  private async start_worker_capture(track: MediaStreamTrack): Promise<void> {
    this.worker = new Worker(
      new URL("./capture_worker.ts", import.meta.url),
      { type: "module" }
    );

    this.worker.onmessage = (e: MessageEvent) => {
      const msg = e.data;
      if (!msg) return;

      if (msg.type === "frame") {
        this.on_worker_frame(msg);
      } else if (msg.type === "error") {
        console.error("CaptureService worker error:", msg.error);
      }
    };

        // MediaStreamTrack is transferable in supporting browsers.
    this.worker.postMessage(
      { type: "start", track },
      [track as unknown as Transferable]
    );
  }

  private on_worker_frame(msg: {
    bytes: ArrayBuffer;
    layout: any;
    coded_width: number;
    coded_height: number;
    display_width: number;
    display_height: number;
  }): void {
    const bytes = new Uint8Array(msg.bytes);
    this.upload_into_slot(bytes, msg.layout, {
      coded_width: msg.coded_width,
      coded_height: msg.coded_height,
      display_width: msg.display_width,
      display_height: msg.display_height,
    }).catch((err) => {
      console.error("CaptureService.on_worker_frame upload failed", err);
    });
  }

  // ---- Shared upload path -------------------------------------------------

  /**
   * Main-thread VideoFrame entry. Copies plane bytes via frame.copyTo,
   * then delegates to upload_into_slot. Always closes the frame.
   */
  private async on_video_frame(frame: VideoFrame): Promise<void> {
      //console.log("[capture] on_video_frame entered, alloc_size=", frame.allocationSize());
    try {
      const alloc_size = frame.allocationSize();
      if (alloc_size <= 1) {
        frame.close();
        return; 
      }

      const buffer = this.yuv_manager.ensure_staging_buffer(alloc_size);
      const layout = await frame.copyTo(buffer);

      await this.upload_into_slot(buffer, layout, {
        coded_width: frame.codedWidth,
        coded_height: frame.codedHeight,
        display_width: frame.displayWidth,
        display_height: frame.displayHeight,
      });
    } catch (err) {
      console.error("CaptureService.on_video_frame failed", err);
    } finally {
      frame.close();
    }
  }

  /**
   * Lazily allocate (or reinitialize) the ColorCameraPool on first frame /
   * resolution change, then write Y / chroma bytes into an Idle slot and
   * publish.
   */
  private async upload_into_slot(
    bytes: Uint8Array,
    layout: any,
    dims: {
      coded_width: number;
      coded_height: number;
      display_width: number;
      display_height: number;
    }
  ): Promise<void> {
    const device = Stage.instance().shader_service.get_device();

    if (!this.has_logged_first_frame) {
    this.has_logged_first_frame = true;
    console.log("[capture] upload_into_slot called", {
      bytes: bytes.byteLength,
      worker_mode: this.worker_mode,
      pool_initialized: this.camera_pool.is_initialized(),
      coded: `${dims.coded_width}x${dims.coded_height}`,
      display: `${dims.display_width}x${dims.display_height}`,
    });
  }
    if (!device) return;

    // Parse plane layout into an upload descriptor.
    const desc: YuvUploadDescriptor = this.yuv_manager.build_descriptor(
      this.camera_info.format,
      layout,
      bytes.byteLength,
      dims.coded_width,
      dims.coded_height,
      dims.display_width,
      dims.display_height
    );

    // Pool init on the very first frame.
    if (!this.camera_pool.is_initialized()) {
      this.initialize_pool_from_descriptor(device, desc);
    }

    // Defensive: rebuild pool if a resolution change slips through.
    if (this.pool_dimensions_changed(desc)) {
      this.camera_pool.shutdown();
      this.initialize_pool_from_descriptor(device, desc);
    }

    // Acquire a slot and write the planes.
    const slot = this.camera_pool.acquire_idle_slot();
    if (!slot) {
      // No free slot — drop this frame. In normal triple-buffer operation
      // this is rare; only happens if a consumer is holding a lease across
      // multiple frames.
      return;
    }
    slot.begin_record();

    try {
      if (desc.format === "NV12") {
        const y = desc.planes[0];
        const uv = desc.planes[1];

        device.queue.writeTexture(
          { texture: slot.y_texture! },
          this.yuv_manager.slice_plane(bytes, y.offset, y.byteLength),
          { offset: 0, bytesPerRow: y.stride, rowsPerImage: y.height },
          { width: y.width, height: y.height, depthOrArrayLayers: 1 }
        );

        device.queue.writeTexture(
          { texture: slot.cbcr_texture! },
          this.yuv_manager.slice_plane(bytes, uv.offset, uv.byteLength),
          { offset: 0, bytesPerRow: uv.stride, rowsPerImage: uv.height },
          { width: uv.width, height: uv.height, depthOrArrayLayers: 1 }
        );
      } else {
        // I420
        const y = desc.planes[0];
        const u = desc.planes[1];
        const v = desc.planes[2];

        device.queue.writeTexture(
          { texture: slot.y_texture! },
          this.yuv_manager.slice_plane(bytes, y.offset, y.byteLength),
          { offset: 0, bytesPerRow: y.stride, rowsPerImage: y.height },
          { width: y.width, height: y.height, depthOrArrayLayers: 1 }
        );
        device.queue.writeTexture(
          { texture: slot.u_texture! },
          this.yuv_manager.slice_plane(bytes, u.offset, u.byteLength),
          { offset: 0, bytesPerRow: u.stride, rowsPerImage: u.height },
          { width: u.width, height: u.height, depthOrArrayLayers: 1 }
        );
        device.queue.writeTexture(
          { texture: slot.v_texture! },
          this.yuv_manager.slice_plane(bytes, v.offset, v.byteLength),
          { offset: 0, bytesPerRow: v.stride, rowsPerImage: v.height },
          { width: v.width, height: v.height, depthOrArrayLayers: 1 }
        );
      }

      // Publish: sequence + timestamp assigned by the pool.
      this.camera_pool.publish_slot(slot, performance.now() / 1000);
    } catch (err) {
      console.error("CaptureService.upload_into_slot: write failed", err);
      this.camera_pool.cancel_slot(slot);
    }
  }

  // ---- Pool sizing helpers -----------------------------------------------

  private initialize_pool_from_descriptor(
    device: GPUDevice,
    desc: YuvUploadDescriptor
  ): void {
    const y_width = desc.planes[0].width;
    const y_height = desc.planes[0].height;

    let chroma_width = 0;
    let chroma_height = 0;
    if (desc.format === "NV12") {
      chroma_width = desc.planes[1].width;
      chroma_height = desc.planes[1].height;
    } else {
      chroma_width = desc.planes[1].width;
      chroma_height = desc.planes[1].height;
    }

    this.camera_pool.initialize(
      device,
      "camera",
      desc.format,
      y_width,
      y_height,
      chroma_width,
      chroma_height
    );

    this.camera_info.set_resolution(
      desc.format,
      desc.displayWidth,
      desc.displayHeight,
      y_width,
      y_height,
      chroma_width,
      chroma_height
    );
  }

  private pool_dimensions_changed(desc: YuvUploadDescriptor): boolean {
    return (
      this.camera_pool.get_y_width() !== desc.planes[0].width ||
      this.camera_pool.get_y_height() !== desc.planes[0].height ||
      this.camera_pool.get_format() !== desc.format
    );
  }

  // ---- Platform detection -------------------------------------------------

  private detect_ios(): boolean {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const is_ios_like =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && (navigator as any).maxTouchPoints > 1);
    return is_ios_like;
  }
}